//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { FlatDefaultIconButton } from '@components/atoms/button/Button';
import CalendarMonth, {
  CALENDAR_MONTH_WIDTH,
} from '@components/date-range-picker/CalendarMonth';
import { useDateRangePickerContext } from '@components/date-range-picker/DateRangePickerContext';
import DateString from '@models/DateString';

interface CalendarViewProps {
  // DateString pointed at the start of a month to show.
  initialDate: DateString;
}

const CalendarView = ({ initialDate }: CalendarViewProps): JSX.Element => {
  const { minDate, maxDate } = useDateRangePickerContext();
  const [visibleDate, setVisibleDate] = useState(initialDate);

  // DateStrings pointed at the start of the month.
  const { leftMonth, rightMonth } = useMemo(() => {
    const nextMonth = DateString.addMonths(visibleDate, 1);
    const prevMonth = DateString.addMonths(visibleDate, -1);

    if (maxDate && DateString.compare(maxDate, nextMonth) <= 0) {
      return { leftMonth: prevMonth, rightMonth: visibleDate };
    }
    return {
      leftMonth: visibleDate,
      rightMonth: nextMonth,
    };
  }, [visibleDate, maxDate]);
  const isNextMonthAvailable = useMemo(
    () =>
      !maxDate ||
      DateString.compare(maxDate, DateString.addMonths(rightMonth, 1)) >= 0,
    [maxDate, rightMonth],
  );
  const isPreviousMonthAvailable = useMemo(
    () => !minDate || DateString.compare(minDate, leftMonth) < 0,
    [minDate, leftMonth],
  );
  return (
    <ViewContainer>
      {isPreviousMonthAvailable && (
        <PrevButton
          iconName='chevron-left'
          onClick={() => setVisibleDate(DateString.addMonths(visibleDate, -1))}
        />
      )}
      {isNextMonthAvailable && (
        <NextButton
          iconName='chevron-right'
          onClick={() => setVisibleDate(DateString.addMonths(visibleDate, 1))}
        />
      )}
      <CalendarContainer>
        <CalendarMonth monthStartDate={leftMonth} />
        <MonthMargin />
        <CalendarMonth monthStartDate={rightMonth} />
      </CalendarContainer>
    </ViewContainer>
  );
};

const PrevButton = styled(FlatDefaultIconButton)(
  ({ theme }) => `
  position: absolute;
  top: 0;
  left: 0;
`,
);
const NextButton = styled(FlatDefaultIconButton)(
  ({ theme }) => `
  position: absolute;
  top: 0;
  right:0;
`,
);

const CalendarContainer = styled.div(
  ({ theme }) => `
  width: ${CALENDAR_MONTH_WIDTH * 2 + parseInt(theme.spacings[4], 10)}px;
  overflow: hidden;
  display: flex;
`,
);

const ViewContainer = styled.div(
  ({ theme }) => `
  display: inline-block;
  position: relative;
`,
);

const MonthMargin = styled.div(
  ({ theme }) => `
  display: inline-block;
  width: ${theme.spacings[4]};
  flex-shrink: 0;
`,
);
export default CalendarView;
